import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchSchedule } from "../../../../../Redux/Slices/scheduleSlice";
import { STATUSES, useConnectionData, useModals } from "../../../../../Components/ConsumerGenerator/ListingContainer/AvailableListing/AvailableListing";
import ViewFinalizeTerms from "../../../../../Components/Modals/ConGen/ViewFinalizeTerms/ViewFinalizeTerms";
import ViewConsent from "../../../../../Components/Modals/ConGen/ViewConsent/ViewConsent";
import UploadDocModal from "../../../../../Components/Modals/ConGen/UploadFiles/UploadFiles";
import ScheduleModal from "../../../../../Components/Modals/Schedule/ScheduleModal";
import UploadLDC from "../../../../../Components/Modals/ConGen/UploadLCD/UploadLDC";
import Charges from "../../../../../Components/Modals/ConGen/Charges/Charges";
import ViewLDC from "../../../../../Components/Modals/ConGen/ViewLDC/ViewLDC";
import Styles from "./ViewButtons.module.css";
import { Schedule } from "@mui/icons-material";
import { getMainScreenConnectionDetails } from "../../../../../Services/BuySell/Listing";


// ---------- LEFT BUTTON ----------
export const LeftButton = React.memo(({ clickedIndex, status, item, source}) => {
  const dispatch = useDispatch();
  const { user, finFinal, bilateralConnections, mainScreenMatchData } = useConnectionData();
  const { modals, toggleModal } = useModals();
    // split "listingIndex-generatorIndex"
  const [listingIndex, generatorIndex] =
  String(clickedIndex).split("-").map(n => Number(n));

  const [finalizeTermsData, setFinalizeTermsData] = useState(null);
  const [loadingTerms, setLoadingTerms] = useState(false);

  const isRA = (item?.tenderType === "RA" || item?.item?.tenderType === "RA");
  const isCB = (item?.tenderType === "CB" || item?.item?.tenderType === "CB");


  const connection = useMemo(() => {
      if (isRA || isCB) {
    // RA always uses the row directly
    return item?.item || item;
  }


    const conn =
      finFinal?.status === STATUSES.TERMS_INITIATED
        ? mainScreenMatchData?.[clickedIndex]
        : item || bilateralConnections?.[clickedIndex];
    return conn || { status: status };
  }, [isRA,isCB, item, finFinal?.status, mainScreenMatchData, bilateralConnections, clickedIndex, status, item, source]);




   const safeSource = source || item || connection || {};

   const handleViewFinalizeTerms = useCallback(async () => {
   if (!connection?.capacityId && !connection?.listingId && !safeSource?.id) return;
   try {
     setLoadingTerms(true);
     const capId = (isRA && (item?.capacityId || item?.item?.capacityId)) ||(isCB && (item?.capacityId || item?.item?.capacityId)) || safeSource.id || connection?.capacityId || connection?.listingId;
     const res = await getMainScreenConnectionDetails(capId);
   
     let apiData=null;
     apiData = res?.data?.data?.page?.[0];
     if(isRA){
      const apiPage = res?.data?.data?.page || [];
      apiData = apiPage[generatorIndex] || apiPage[0];
     }
     if (apiData) {
       setFinalizeTermsData(apiData);
       toggleModal("terms", true);
     }
   } catch (err) {
     console.error("Error fetching finalized terms:", err);
   } finally {
     setLoadingTerms(false);
  }
 }, [connection]);

 useEffect(() => {
  let mounted = true;

  const fetchStatus = async () => {
    const capId =
      (isRA && (item?.capacityId || item?.item?.capacityId)) ||
      (isCB && (item?.capacityId || item?.item?.capacityId)) ||
      safeSource?.id ||
      connection?.capacityId ||
      connection?.listingId;

    if (!capId) return;

    try {
      const res = await getMainScreenConnectionDetails(capId);
      const apiPage = res?.data?.data?.page || [];
      const apiData = isRA
        ? apiPage[generatorIndex] || apiPage[0]
        : apiPage[0];

      if (mounted && apiData) {
        setFinalizeTermsData(apiData);
      }
    } catch (e) {
      console.error("Error prefetching status:", e);
    }
  };

  fetchStatus();

  return () => {
    mounted = false;
  };
}, [clickedIndex, isCB, isRA]);


 const isScheduledFromApi =
  finalizeTermsData?.status === STATUSES.SCHEDULED;


  const safeConnectionId = useMemo(() => {
  if (isRA || isCB) {
    // RA case: use bilateralContractId
    return item?.bilateralConnection?.[0]?.bilateralContractId 
        || item?.item?.bilateralConnection?.[0]?.bilateralContractId;
  }

  // Non-RA: use listingId normally
  return connection?.listingId;
}, [isRA, isCB, item, connection]);


  const handleViewSchedule = useCallback(() => {
    const id = safeConnectionId || connection?.listingId;
    if (!id) return;
    dispatch(fetchSchedule(id));
    toggleModal("schedule", true);
  }, [connection?.listingId, safeConnectionId, dispatch, toggleModal]);

  const buttonConfig = useMemo(() => [
    { key: "terms", show: true, text: "View Finalized Terms", onClick: handleViewFinalizeTerms },
    { key: "consent", show: true, text: "View Consent" },
    { key: "docs", show: true, text: "View Docs" },
    { key: "charges", show: true, text: "View Charge" },
    { key: "schedule", show: connection?.status === STATUSES.SCHEDULED || isScheduledFromApi, text: "View Schedule", onClick: handleViewSchedule },
  ], [connection, handleViewSchedule, handleViewFinalizeTerms, isScheduledFromApi]);

  const modalMapping = {
  terms: <ViewFinalizeTerms open={modals.terms} handleClose={() => toggleModal("terms", false) }
      data={{ 
      bilateralConnections: finalizeTermsData?.bilateralConnections || [],
      capacityConsumer: finalizeTermsData?.capacityConsumer || {},
      capacityGenerator: finalizeTermsData?.capacityGenerator || {},
     listingId:(isRA && (item?.capacityId || item?.item?.capacityId)) || (isCB && (item?.capacityId || item?.item?.capacityId)) || safeSource.id,
      listingName: finalizeTermsData?.listingName || "",
      ...finalizeTermsData,}}
    />,
    consent: <ViewConsent connectionId={safeConnectionId} open={modals.consent} onClose={() => toggleModal("consent", false)} />,
    docs: <UploadDocModal type="view" connectionId={safeConnectionId} open={modals.docs} handleClose={() => toggleModal("docs", false)} />,
    schedule: <ScheduleModal open={modals.schedule} handleClose={() => toggleModal("schedule", false)} connectionId={safeConnectionId} mode="view"/>,
  };

 
  return (
    <>
      {Object.entries(modalMapping).map(([key, modal]) => modals[key] && modal)}

        {modals.charges && (<Charges
    contractId={safeConnectionId}
    open={true}
    onClose={() => toggleModal("charges", false)}
  />
)}

      <div className={Styles.leftSideBtnContainer}>
        {buttonConfig.filter(btn => btn.show).map((btn, index) => (
          <button
            key={`left-btn-${index}`}
            onClick={btn.onClick ?? (() => toggleModal(btn.key, true))}
            className={Styles.leftSideBtn}
          >
            {btn.text}
          </button>
        ))}
      </div>
    </>
  );
});

// ---------- RIGHT BUTTON ----------
export const RightButton = React.memo(({ clickedIndex, status, sourceId, item}) => {
  const { finFinal, bilateralConnections, mainScreenMatchData } = useConnectionData();
  const { modals, toggleModal } = useModals();

  const connection = useMemo(() => {
    return finFinal?.status === STATUSES.TERMS_INITIATED
      ? mainScreenMatchData?.[clickedIndex]
      : bilateralConnections?.[clickedIndex] || { status };
  }, [finFinal?.status, mainScreenMatchData, bilateralConnections, clickedIndex, status]);

  const isScheduled = connection?.status === STATUSES.SCHEDULED;

  const isRA = (item?.tenderType === "RA" || item?.item?.tenderType === "RA");
  const isCB = (item?.tenderType === "CB" || item?.item?.tenderType === "CB");



  const safeConnectionId = useMemo(() => {
  if (isRA || isCB) {
    // RA case: use bilateralContractId
    return item?.bilateralConnection?.[0]?.bilateralContractId 
        || item?.item?.bilateralConnection?.[0]?.bilateralContractId;
  }

  // Non-RA: use listingId normally
  return item?.item?.id || item?.id;
}, [isRA, isCB, item]);


const safeCapacityId = useMemo(() => {
  if (isRA || isCB) {
    // RA case: use bilateralContractId
    return item?.item?.capacityId;
  }

  // Non-RA: use listingId normally
  return item?.source?.id;
}, [isRA, isCB, item]);


  const handleGenerateSchedule = () => {
    toggleModal("uploadLDC", true);
  };
  
  return (
    <>
      {modals.uploadLDC && (
        <UploadLDC
          connectionId={safeConnectionId || item?.item?.id || item?.id } 
          capacityId={safeCapacityId || item?.source?.id}
          open={modals.uploadLDC}
          onClose={() => toggleModal("uploadLDC", false)}
        />
      )}

      <div className={Styles.rightSideBtnContainer}>
        {isScheduled ? (
          // <button onClick={() => toggleModal("schedule", false)} className={Styles.rightSideBtn}>
          //   Close
          // </button>
          console.log("close")
        ) : (
          <button onClick={handleGenerateSchedule} className={Styles.rightSideBtn}>
            Generate Schedule
          </button>
        )}
      </div>
    </>
  );
});
